import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Typography } from 'components';
import { AttendanceTrackingPageHeader } from 'components/molecules/AttendanceTrackingPageHeader';
import { agent } from 'services/service';
import { LeaveAllocationEmployeeDTO } from 'services/LeaveAllocation';
import { toast } from 'react-toastify';
import './index.scss';

type LeaveField = 'annualLeave' | 'casualLeave' | 'lieuLeave' | 'medicalLeave';

const LEAVE_FIELDS: { key: LeaveField; label: string }[] = [
  { key: 'annualLeave', label: 'Annual' },
  { key: 'casualLeave', label: 'Casual' },
  { key: 'lieuLeave', label: 'Lieu' },
  { key: 'medicalLeave', label: 'Medical' }
];

const CURRENT_YEAR = new Date().getFullYear();

export const ManagerLeaveAllocation = () => {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [rows, setRows] = useState<LeaveAllocationEmployeeDTO[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [yearError, setYearError] = useState('');

  const prevYear = useRef(year);

  const loadAllocations = useCallback(async (y: number, searchTerm = '') => {
    setIsLoading(true);
    try {
      const res = await agent.LeaveAllocationService.getEmployeeAllocations(
        y,
        searchTerm || undefined
      );
      setRows(res.data);
      setHasLoadedOnce(true);
    } catch {
      toast.error('Failed to load leave allocations');
      setHasLoadedOnce(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(
      () => {
        loadAllocations(year, search.trim());
      },
      search.trim() ? 300 : 0
    );
    return () => window.clearTimeout(timer);
    // Reload on search only; year changes are handled on blur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, loadAllocations]);

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const val = parseInt(raw);
    if (raw === '' || isNaN(val)) {
      setYearError('Please enter a valid year');
      return;
    }
    if (val < CURRENT_YEAR) {
      setYear(val);
      setYearError(`Year must be ${CURRENT_YEAR} or later`);
      return;
    }
    if (val > 2100) {
      setYear(val);
      setYearError('Year must be 2100 or earlier');
      return;
    }
    setYear(val);
    setYearError('');
  };

  const handleYearBlur = () => {
    if (yearError) return;
    if (year !== prevYear.current) {
      prevYear.current = year;
      setSearch('');
      loadAllocations(year);
    }
  };

  const handleYearKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className="manager-leave-allocation-page">
      <AttendanceTrackingPageHeader
        title="Leave Allocation"
        description="View only — annual leave allocations are managed by HR."
        crumbs={[{ label: 'Manager Portal' }, { label: 'Leave Allocation' }]}
        action={
          <div className="year-field">
            <label className="year-label">
              <Typography type="base_medium" text="Year" />
            </label>
            <input
              type="number"
              className={`year-input${yearError ? ' error' : ''}`}
              value={year}
              onChange={handleYearChange}
              onBlur={handleYearBlur}
              onKeyDown={handleYearKeyDown}
              min={CURRENT_YEAR}
              max={2100}
            />
            {yearError && <span className="year-error">{yearError}</span>}
          </div>
        }
      />

      <div className="allocation-card">
        <div className="allocation-toolbar">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or EPF..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {isLoading && !hasLoadedOnce ? (
          <div className="loading-state">
            <Typography type="base_regular" text="Loading allocations..." />
          </div>
        ) : (
          <div className="table-scroll">
            <table className="allocation-table">
              <thead>
                <tr>
                  <th className="col-epf">EPF</th>
                  <th className="col-name">Employee</th>
                  {LEAVE_FIELDS.map((f) => (
                    <th key={f.key} className="col-leave">
                      {f.label} Leave
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty-state">
                      {search
                        ? 'No employees match your search.'
                        : 'No employees found.'}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.upn}>
                      <td className="col-epf">{row.epfNumber}</td>
                      <td className="col-name">{row.employeeName}</td>
                      {LEAVE_FIELDS.map((f) => (
                        <td key={f.key} className="col-leave">
                          <span className="leave-count-chip">{row[f.key]}</span>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
